import { Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { Box, CssBaseline, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { CollectionsProvider } from '../contexts/CollectionsContext';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

// Route guards
interface AdminRouteProps {
  children: React.ReactNode;
}
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, userRole, loading } = useAuth();
  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (userRole !== 'admin') return <Navigate to="/dashboard" replace />;
  return (
    <CollectionsProvider>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Header />
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', mt: 8 }}>
          {children}
        </Box>
      </Box>
    </CollectionsProvider>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <CollectionsProvider>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Header />
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', mt: 8 }}>
          {children}
        </Box>
      </Box>
    </CollectionsProvider>
  );
};

// Loadable wrapper
const Loadable = (Component: React.ComponentType<any>) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

// Lazy imports for all main pages
const Dashboard = Loadable(lazy(() => import('../pages/Dashboard')));
const Books = Loadable(lazy(() => import('../pages/Books')));
const BookForm = Loadable(lazy(() => import('../pages/BookForm')));
const BookDetails = Loadable(lazy(() => import('../pages/BookDetails')));
const MeditationDetails = Loadable(lazy(() => import('../pages/MeditationDetails')));
const AudioDetails = Loadable(lazy(() => import('../pages/AudioDetails')));
const Videos = Loadable(lazy(() => import('../pages/Videos')));
const VideoForm = Loadable(lazy(() => import('../pages/VideoForm')));
const Audios = Loadable(lazy(() => import('../pages/Audios')));
const AudioForm = Loadable(lazy(() => import('../pages/AudioForm')));
const Meditations = Loadable(lazy(() => import('../pages/Meditations')));
const MeditationForm = Loadable(lazy(() => import('../pages/MeditationForm')));
const Songs = Loadable(lazy(() => import('../pages/Songs')));
const SongForm = Loadable(lazy(() => import('../pages/SongForm')));
const SongDetails = Loadable(lazy(() => import('../pages/SongDetails')));
const Tales = Loadable(lazy(() => import('../pages/Tales')));
const TaleForm = Loadable(lazy(() => import('../pages/TaleForm')));
const TaleDetails = Loadable(lazy(() => import('../pages/TaleDetails')));
const Lullabies = Loadable(lazy(() => import('../pages/Lullabies')));
const LullabyForm = Loadable(lazy(() => import('../pages/LullabyForm')));
const LullabyDetails = Loadable(lazy(() => import('../pages/LullabyDetails')));
const Users = Loadable(lazy(() => import('../pages/Users')));
const Settings = Loadable(lazy(() => import('../pages/Settings')));
const Promocodes = Loadable(lazy(() => import('../pages/Promocodes')));
const PromocodeForm = Loadable(lazy(() => import('../pages/PromocodeForm')));
const PromocodeDetails = Loadable(lazy(() => import('../pages/PromocodeDetails')));
const AuthUsers = Loadable(lazy(() => import('../pages/AuthUsers')));
const AuthUserForm = Loadable(lazy(() => import('../pages/AuthUserForm')));
const AuthUserDetails = Loadable(lazy(() => import('../pages/AuthUserDetails')));
const WhitelistVerifyEmails = Loadable(lazy(() => import('../pages/WhitelistVerifyEmails')));
const AppUserDetails = Loadable(lazy(() => import('../pages/AppUserDetails')));
const Documentation = Loadable(lazy(() => import('../pages/Documentation')));
const Languages = Loadable(lazy(() => import('../pages/Languages')));
const LanguageForm = Loadable(lazy(() => import('../pages/LanguageForm')));
const LanguageTranslations = Loadable(lazy(() => import('../pages/LanguageTranslations')));
const Login = Loadable(lazy(() => import('../pages/Login')));
const VideoDetails = Loadable(lazy(() => import('../pages/VideoDetails')));

export default function AppRoutes() {
  return useRoutes([
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: '/dashboard',
      element: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: '/books',
      element: (
        <ProtectedRoute>
          <Books />
        </ProtectedRoute>
      ),
    },
    {
      path: '/books/create',
      element: (
        <ProtectedRoute>
          <BookForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/books/:id/edit',
      element: (
        <ProtectedRoute>
          <BookForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/books/:id',
      element: (
        <ProtectedRoute>
          <BookDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/videos',
      element: (
        <ProtectedRoute>
          <Videos />
        </ProtectedRoute>
      ),
    },
    {
      path: '/videos/create',
      element: (
        <ProtectedRoute>
          <VideoForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/videos/:id/edit',
      element: (
        <ProtectedRoute>
          <VideoForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/videos/:id',
      element: (
        <ProtectedRoute>
          <VideoDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/audios',
      element: (
        <ProtectedRoute>
          <Audios />
        </ProtectedRoute>
      ),
    },
    {
      path: '/audios/create',
      element: (
        <ProtectedRoute>
          <AudioForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/audios/:id/edit',
      element: (
        <ProtectedRoute>
          <AudioForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/songs',
      element: (
        <ProtectedRoute>
          <Songs />
        </ProtectedRoute>
      ),
    },
    {
      path: '/songs/create',
      element: (
        <ProtectedRoute>
          <SongForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/songs/:id/edit',
      element: (
        <ProtectedRoute>
          <SongForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/songs/:id',
      element: (
        <ProtectedRoute>
          <SongDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/tales',
      element: (
        <ProtectedRoute>
          <Tales />
        </ProtectedRoute>
      ),
    },
    {
      path: '/tales/create',
      element: (
        <ProtectedRoute>
          <TaleForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/tales/:id/edit',
      element: (
        <ProtectedRoute>
          <TaleForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/tales/:id',
      element: (
        <ProtectedRoute>
          <TaleDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/lullabies',
      element: (
        <ProtectedRoute>
          <Lullabies />
        </ProtectedRoute>
      ),
    },
    {
      path: '/lullabies/create',
      element: (
        <ProtectedRoute>
          <LullabyForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/lullabies/:id/edit',
      element: (
        <ProtectedRoute>
          <LullabyForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/lullabies/:id',
      element: (
        <ProtectedRoute>
          <LullabyDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/meditations',
      element: (
        <ProtectedRoute>
          <Meditations />
        </ProtectedRoute>
      ),
    },
    {
      path: '/meditations/create',
      element: (
        <ProtectedRoute>
          <MeditationForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/meditations/:id/edit',
      element: (
        <ProtectedRoute>
          <MeditationForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/meditations/:id',
      element: (
        <ProtectedRoute>
          <MeditationDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/audios/:id',
      element: (
        <ProtectedRoute>
          <AudioDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/users',
      element: (
        <ProtectedRoute>
          <Users />
        </ProtectedRoute>
      ),
    },
    {
      path: '/users/:id',
      element: (
        <ProtectedRoute>
          <AppUserDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/settings',
      element: (
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      ),
    },
    {
      path: '/promocodes',
      element: (
        <ProtectedRoute>
          <Promocodes />
        </ProtectedRoute>
      ),
    },
    {
      path: '/promocodes/create',
      element: (
        <ProtectedRoute>
          <PromocodeForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/promocodes/:id/edit',
      element: (
        <ProtectedRoute>
          <PromocodeForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/promocodes/:id',
      element: (
        <ProtectedRoute>
          <PromocodeDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: '/documentation',
      element: (
        <ProtectedRoute>
          <Documentation />
        </ProtectedRoute>
      ),
    },
    {
      path: '/documentation/user-guide',
      element: (
        <ProtectedRoute>
          <Documentation />
        </ProtectedRoute>
      ),
    },
    {
      path: '/documentation/api',
      element: (
        <ProtectedRoute>
          <Documentation />
        </ProtectedRoute>
      ),
    },
    {
      path: '/languages',
      element: (
        <ProtectedRoute>
          <Languages />
        </ProtectedRoute>
      ),
    },
    {
      path: '/languages/create',
      element: (
        <ProtectedRoute>
          <LanguageForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/languages/:key/edit',
      element: (
        <ProtectedRoute>
          <LanguageForm />
        </ProtectedRoute>
      ),
    },
    {
      path: '/language-translations',
      element: (
        <ProtectedRoute>
          <LanguageTranslations />
        </ProtectedRoute>
      ),
    },
    {
      path: '/auth-users',
      element: (
        <AdminRoute>
          <AuthUsers />
        </AdminRoute>
      ),
    },
    {
      path: '/auth-users/create',
      element: (
        <AdminRoute>
          <AuthUserForm />
        </AdminRoute>
      ),
    },
    {
      path: '/auth-users/:id/edit',
      element: (
        <AdminRoute>
          <AuthUserForm />
        </AdminRoute>
      ),
    },
    {
      path: '/auth-users/:id',
      element: (
        <AdminRoute>
          <AuthUserDetails />
        </AdminRoute>
      ),
    },
    {
      path: '/whitelist-verify-emails',
      element: (
        <AdminRoute>
          <WhitelistVerifyEmails />
        </AdminRoute>
      ),
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}
